import React, { createContext, useContext, useEffect, useState } from "react";
import { io } from "socket.io-client";
import { baseUrl } from "../config/config";

const AdminContext = createContext();
export const useAdminContext = () => useContext(AdminContext);

/**
 * Admin Context and Socket Management
 *
 * - **AdminContext**: Provides global state management for admin-related data (e.g., `navbarTitle`).
 * - **Socket.io**: Establishes and manages the socket connection for real-time communication with the backend.
 *
 * **Functions:**
 * - **START_SOCKET_FORCEFULLY**: Initializes the socket connection with an admin role, using the `user_id` and `role=admin` in the query string.
 * - **STOP_SOCKET_FORCEFULLY**: Disconnects the socket, emits an "off_line" event, and ensures the user is logged off from the socket.
 *
 * **ContextAdmin**: Wraps the application with `AdminContext` and provides access to socket management and navbar title state.
 */

export function ContextAdmin({ children }) {
  const [navbarTitle, setnavbarTitle] = useState("");
  const [socket, setSocket] = useState(null);

  // To handle the socket connection lifecycle
  useEffect(() => {
    const id = localStorage.getItem("UserId");

    if (id) {
      // Create a new socket connection when the user ID is available
      const newSocket = io(baseUrl + `?user_id=${id}`);
      setSocket(newSocket);

      // Cleanup function to disconnect the socket when the component is unmounted
      return () => {
        if (newSocket) {
          newSocket.emit("off_line", { user_id: id });
          newSocket.disconnect();
        }
      };
    }
  }, []); // Empty dependency array to run this effect only once

  const START_SOCKET_FORCEFULLY = (id) => {
    if (!socket) {
      const newSocket = io(baseUrl + `?user_id=${id}`);
      setSocket(newSocket);
    }
  };

  const STOP_SOCKET_FORCEFULLY = () => {
    if (socket) {
      const id = localStorage.getItem("UserId");
      socket.emit("off_line", { user_id: id });
      socket.disconnect();
      setSocket(null); // Clear the socket instance after disconnect
    }
  };

  const collection = {
    navbarTitle,
    setnavbarTitle,
    START_SOCKET_FORCEFULLY,
    STOP_SOCKET_FORCEFULLY,
    socket,
  };

  return (
    <AdminContext.Provider value={collection}>{children}</AdminContext.Provider>
  );
}
